import { useEffect, useRef, useState } from "react";
import { IApiTokenBody } from "../../../../../@types/api-token.type";
import { ButtonDeleted } from "../../../../../components/ButtonDeleted";
import dayjs from "dayjs";
import { formatDateOfLastAccess } from "../../../../../utils/formatDate";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import { themeGrid } from "../../../../../themeGrid";
import { localeText } from "../../../../../localeTextFromGrid";
import { ColDef, FilterChangedEvent } from "ag-grid-community";
import secureLocalStorage from "react-secure-storage";
import styles from "./styles.module.scss";

interface TokenDataGridProps {
  rows: IApiTokenBody[];
  rowCount: number;
  onDelete: (id: string) => void;
  loading: boolean;
}

export const TokenDataGrid: React.FC<TokenDataGridProps> = ({
  rows,
  onDelete,
  loading,
}) => {
  const columns: ColDef[] = [
    {
      field: "name",
      headerName: "Nome do Token",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "createdAt",
      headerName: "Criado",
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params.data.createdAt,
      cellRenderer: (params: CustomCellRendererProps) => {
        return dayjs(params.value).format("DD/MM/YYYY");
      },
      filter: "agDateColumnFilter",
      filterParams: {
        comparator: (filterLocalDateAtMidnight: any, cellValue: any) => {
          const cellDate = dayjs(cellValue).startOf("day").toDate();
          if (cellDate < filterLocalDateAtMidnight) return -1;
          if (cellDate > filterLocalDateAtMidnight) return 1;
          return 0;
        },
      },
    },
    {
      field: "dateOfLastAccess",
      headerName: "Último Acesso",
      flex: 1,
      minWidth: 150,
      filter: false,
      sortable: false,
      cellRenderer: (params: CustomCellRendererProps) => {
        return formatDateOfLastAccess(params.value);
      },
    },
    {
      field: "action",
      headerName: "Ação",
      flex: 0.5,
      minWidth: 70,
      filter: false,
      sortable: false,
      cellRenderer: (params: CustomCellRendererProps) => (
        <div style={{ display: "flex", alignItems: "center", height: "100%" }}>
          <ButtonDeleted handleDeleteItem={() => onDelete(params.data.id)} />
        </div>
      ),
    },
  ];

  const gridRef = useRef<AgGridReact<any>>(null);

  const getFromCache = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFilters, setCachedFilters] = useState(
    getFromCache("agGridFiltersApiToken")
  );
  const [cachedSort, setCachedSort] = useState(
    getFromCache("agGridSortApiToken")
  );

  const saveToCache = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCache("agGridFiltersApiToken", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCache("agGridSortApiToken", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFilters) {
        gridApi.setFilterModel(cachedFilters);
      }

      // Restaurar ordenação
      if (cachedSort) {
        gridApi.applyColumnState({
          state: cachedSort,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFilters, cachedSort, columns]);

  return (
    <div className={`${styles.dataGridContainer} `}>
      <AgGridReact
        ref={gridRef}
        rowData={rows}
        columnDefs={columns}
        theme={themeGrid}
        localeText={localeText}
        loading={loading}
        overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
        defaultColDef={{
          sortable: true,
          editable: false,
          filter: true,
          floatingFilter: true,
        }}
        suppressCellFocus={true}
        onFilterChanged={onFilterChanged}
        onSortChanged={onSortChanged}
        animateRows={true}
      />
    </div>
  );
};
